import { collection, doc, getDoc, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { linkingCodesService } from './linkingCodesService';
import { studentsService } from './firestoreService';

export interface SearchUser {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: 'student' | 'parent' | 'teacher';
  isActive: boolean;
  photoURL?: string;
  children?: string[];
  parentId?: string;
  teacherId?: string;
  grade?: string;
  subjects?: string[];
  createdAt: Date;
}

export interface SearchOptions {
  role?: 'student' | 'parent' | 'teacher';
  isActive?: boolean;
  limit?: number;
  excludeIds?: string[];
}

export interface SearchResult {
  users: SearchUser[];
  total: number;
  hasMore: boolean;
}

export interface CodeSearchResult {
  found: boolean;
  user?: SearchUser;
  codeData?: any;
  error?: string;
}

class UserSearchService {
  async searchByCode(code: string): Promise<CodeSearchResult> {
    try {
      const codeValidation = await linkingCodesService.validateCode(code);
      if (!codeValidation.isValid || !codeValidation.linkingCode) {
        return {
          found: false,
          error: codeValidation.error || 'الكود غير موجود',
        };
      }

      const linkingCode = codeValidation.linkingCode;
      const user = await this.searchInUsers(
        linkingCode.userId,
        linkingCode.userType
      );

      if (!user) {
        return {
          found: false,
          error: 'المستخدم غير موجود رغم وجود الكود',
        };
      }

      return {
        found: true,
        user,
        codeData: linkingCode,
      };
    } catch (error) {
      console.error('Error searching by code:', error);
      return {
        found: false,
        error: error instanceof Error ? error.message : 'خطأ في البحث',
      };
    }
  }

  private async searchInUsers(
    userId: string,
    expectedRole?: string
  ): Promise<SearchUser | null> {
    try {
      const userDocument = await getDoc(doc(db, 'users', userId));
      if (!userDocument.exists()) return null;

      const data = userDocument.data();
      if (expectedRole && data.role !== expectedRole) return null;

      return this.mapUserDocument(userDocument.id, data);
    } catch (error) {
      console.error('Error searching in users collection:', error);
      return null;
    }
  }

  async searchUsers(
    searchQuery: string,
    options: SearchOptions = {}
  ): Promise<SearchResult> {
    try {
      const {
        role,
        isActive = true,
        limit: searchLimit = 20,
        excludeIds = [],
      } = options;

      const cleanQuery = searchQuery.trim().toLowerCase();
      if (!cleanQuery && !options.role) {
        return { users: [], total: 0, hasMore: false };
      }

      const constraints = [];
      if (isActive !== undefined) constraints.push(where('isActive', '==', isActive));
      if (role) constraints.push(where('role', '==', role));

      const usersQuery = constraints.length
        ? query(collection(db, 'users'), ...constraints, orderBy('name'))
        : query(collection(db, 'users'), orderBy('name'));

      const usersSnapshot = await getDocs(usersQuery);
      const users = usersSnapshot.docs
        .map(userDocument => this.mapUserDocument(userDocument.id, userDocument.data()))
        .filter(user => !excludeIds.includes(user.id))
        .filter(user =>
          cleanQuery
            ? [user.name, user.email, user.phone || '']
                .join(' ')
                .toLowerCase()
                .includes(cleanQuery)
            : true
        )
        .slice(0, searchLimit);

      return {
        users,
        total: users.length,
        hasMore: users.length === searchLimit,
      };
    } catch (error) {
      console.error('Error searching users:', error);
      return { users: [], total: 0, hasMore: false };
    }
  }

  async searchAvailableParents(
    searchQuery: string,
    excludeStudentId?: string
  ): Promise<SearchUser[]> {
    const result = await this.searchUsers(searchQuery, {
      role: 'parent',
      isActive: true,
      limit: 15,
    });

    if (!excludeStudentId) return result.users;

    return result.users.filter(
      parent => !(parent.children || []).includes(excludeStudentId)
    );
  }

  async getParentChildren(parentId: string): Promise<SearchUser[]> {
    try {
      const tenantChildren = await studentsService.getByParentId(parentId);
      const children = await Promise.all(
        tenantChildren.map(async child => {
          const userDocument = await getDoc(doc(db, 'users', child.id));
          const userData = userDocument.exists() ? userDocument.data() : {};
          return {
            id: child.id,
            name: child.name || userData.name || '',
            email: child.email || userData.email || '',
            phone: child.phone || userData.phone || '',
            role: 'student' as const,
            isActive: child.isActive !== false,
            photoURL: userData.photoURL,
            parentId,
            teacherId: child.teacherId,
            grade: child.grade,
            createdAt: child.createdAt?.toDate?.() || new Date(),
          };
        })
      );

      return children;
    } catch (error) {
      console.error('Error getting parent children:', error);
      return [];
    }
  }

  async advancedSearch(filters: {
    query?: string;
    role?: 'student' | 'parent' | 'teacher';
    grade?: string;
    teacherId?: string;
    hasParent?: boolean;
    isActive?: boolean;
    limit?: number;
  }): Promise<SearchResult> {
    const baseResult = await this.searchUsers(filters.query || ' ', {
      role: filters.role,
      isActive: filters.isActive,
      limit: filters.limit,
    });

    const cleanQuery = (filters.query || '').trim().toLowerCase();
    let users = baseResult.users;

    if (!cleanQuery && filters.role) {
      const usersQuery = query(
        collection(db, 'users'),
        where('role', '==', filters.role),
        orderBy('name')
      );
      const usersSnapshot = await getDocs(usersQuery);
      users = usersSnapshot.docs.map(userDocument =>
        this.mapUserDocument(userDocument.id, userDocument.data())
      );
    }

    if (filters.grade) {
      users = users.filter(user => user.grade === filters.grade);
    }
    if (filters.teacherId) {
      users = users.filter(user => user.teacherId === filters.teacherId);
    }
    if (filters.hasParent !== undefined) {
      users = users.filter(user => Boolean(user.parentId) === filters.hasParent);
    }

    const limitedUsers = users.slice(0, filters.limit || 20);
    return {
      users: limitedUsers,
      total: limitedUsers.length,
      hasMore: users.length > limitedUsers.length,
    };
  }

  async getTeachers(): Promise<SearchUser[]> {
    const result = await this.searchUsers(' ', {
      role: 'teacher',
      isActive: true,
      limit: 100,
    });
    return result.users;
  }

  async getStudents(): Promise<SearchUser[]> {
    const result = await this.searchUsers(' ', {
      role: 'student',
      isActive: true,
      limit: 100,
    });
    return result.users;
  }

  async getParents(): Promise<SearchUser[]> {
    const result = await this.searchUsers(' ', {
      role: 'parent',
      isActive: true,
      limit: 100,
    });
    return result.users;
  }

  private mapUserDocument(id: string, data: any): SearchUser {
    const user: SearchUser = {
      id,
      name: data.name || '',
      email: data.email || '',
      phone: data.phone || '',
      role: data.role || 'student',
      isActive: data.isActive ?? true,
      photoURL: data.photoURL || '',
      createdAt: data.createdAt?.toDate?.() || new Date(),
    };

    if (data.role === 'parent') {
      user.children = data.parentProfile?.children || [];
    } else if (data.role === 'student') {
      user.grade = data.studentProfile?.grade;
      user.parentId = data.studentProfile?.parentId;
    } else if (data.role === 'teacher') {
      user.subjects = data.teacherProfile?.subjects || [];
    }

    return user;
  }
}

export const userSearchService = new UserSearchService();
export default userSearchService;
